import { SlotRendering } from '@frontend/common-types';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { PackageStateName, PackageTriggerName, PackageType } from '@frontend/package/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot, SlotType } from '@frontend/slot/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import React, { useEffect, useState } from 'react';

import { SlotLayoutCell, slotLayoutDisplayNumber } from '../../../components/slot-layout-cell';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    accountId?: string;
    spotId?: string;
    currentModule: Module | null;
    modules: Module[] | null;
    selectedSlot: Slot | null;
    setCurrentModule: (module: Module | null) => void;
    slotComponent: (slotRendering: SlotRendering) => React.ReactNode;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;

    selectModuleModalOpen: boolean;
    setSelectModuleModalOpen: (open: boolean) => void;
}

const usePickUpConfirmOpenSlot = (): ViewProps => {
    const { navigationState, store, onBack, onSubmit: onContinue } = useScreen();
    const { transaction } = useWizardTransaction({
        wizardId: store!.wizard.id,
        store
    });
    const { package: _package, localUpdatePackage } = useWizardPackage({
        wizardId: store!.wizard.id,
        store
    });

    const [modules, setModules] = useState<Module[] | null>(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState<number>(0);

    const [allSlots, setAllSlots] = useState<Slot[] | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

    const [selectModuleModalOpen, setSelectModuleModalOpen] = useState<boolean>(false);

    // Fetch spot and modules
    useEffect(() => {
        const accountId = navigationState.iot?.account_id || _package?.account_id || transaction?.account_id;
        const spotId = navigationState.iot?.spot_id || _package?.spot_id || transaction?.spot_id;
        const moduleId = _package?.module_id || transaction?.module_id;

        if (accountId && spotId) {
            ModuleClient.fetchModules({ spot_id: spotId }).then((result) => {
                // Sort modules by name using ASCII comparison (handles both numeric and alphabetic: 1, 2, 3... or a, b, c...)
                const sortedModules = result.results.sort((a, b) => {
                    const indexA = a.index || '';
                    const indexB = b.index || '';
                    return indexA.localeCompare(indexB, undefined, { numeric: true, sensitivity: 'base' });
                });
                setModules(sortedModules);
                // Find the module that contains a package's slot
                if (moduleId) {
                    const moduleIndex = sortedModules.findIndex((m) => m.id === moduleId);
                    if (moduleIndex >= 0) {
                        setCurrentModuleIndex(moduleIndex);
                    }
                }
            });

            SlotClient.fetchSlots({ spot_id: spotId, size: '1000', index: '0' }).then((result) => {
                setAllSlots(result.results);
            });
        }
    }, []);

    useEffect(() => {
        if (!_package && transaction) {
            PackageClient.fetchPackages({
                account_id: navigationState.iot!.account_id,
                spot_id: navigationState.spot?.id,
                type: PackageType.PICK_UP,
                transaction_id: transaction.id
            }).then((response) => {
                const pkg = response.results
                    .filter(
                        (p) =>
                            ![
                                PackageStateName.PACKAGE_PICK_UP_DONE,
                                PackageStateName.PACKAGE_PICK_UP_CANCELLED,
                                PackageStateName.PACKAGE_PICK_UP_SKIPPED
                            ].includes(p.state)
                    )
                    .sort((a, b) => a.priority - b.priority)[0];
                if (pkg) localUpdatePackage(pkg);
            });
        }
    }, [transaction, _package]);

    useEffect(() => {
        if (allSlots && store && modules) {
            if (_package?.slot_id) {
                const selected = allSlots.find((slot) => slot.id === _package.slot_id);
                if (selected) {
                    setSelectedSlot(selected);
                    setCurrentModuleIndex(modules.findIndex((m) => m.id === selected.module_id));
                }
            } else if (transaction?.slot_id) {
                const selected = allSlots.find((slot) => slot.id === transaction.slot_id);
                if (selected) {
                    setSelectedSlot(selected);
                    setCurrentModuleIndex(modules.findIndex((m) => m.id === selected.module_id));
                }
            }
        }
    }, [allSlots, modules, _package, transaction]);

    const currentModule = modules && modules.length > 0 ? modules[currentModuleIndex] : null;

    const setCurrentModule = (module: Module | null) => {
        if (modules && module) {
            const index = modules.findIndex((m) => m.id === module.id);
            if (index >= 0) {
                setCurrentModuleIndex(index);
            }
        }
        setSelectModuleModalOpen(false);
    };

    const slotComponent = (slotRendering: SlotRendering): React.ReactNode => {
        const slot = allSlots ? allSlots.find((s) => s.id === slotRendering.id) : null;
        const isHighlighted = slot?.id === selectedSlot?.id;
        const slotNumber = slotLayoutDisplayNumber(slot);

        return (
            <SlotLayoutCell
                slotNumber={slotNumber}
                isSelected={!!isHighlighted}
                isDisabled={slot?.type === SlotType.TERMINAL}
                hasContent={false}
                onSelect={isHighlighted ? onSubmit : undefined}
            />
        );
    };

    const onSubmit = async () => {
        if (!store || !_package || !_package.account_id || !_package.transaction_id || !_package.id || !selectedSlot) return;

        await PackageWorkflowClient.triggerPackageState(_package.account_id, _package.transaction_id, _package.id, {
            trigger: PackageTriggerName.PACKAGE_OPEN_SLOT,
            location: navigationState.spot?.id ?? undefined,
            actor: navigationState.user?.id ?? undefined
        }).finally(() => {
            const nextStep = store.steps.find((step) => step.screen === ScreenType.PICK_UP_CONFIRM_OPEN_SLOT && step.trigger === ScreenTrigger.SUBMIT);
            onContinue(nextStep);
        });
    };

    return {
        accountId: navigationState.iot?.account_id || _package?.account_id || transaction?.account_id || undefined,
        spotId: navigationState.iot?.spot_id || _package?.spot_id || transaction?.spot_id || undefined,
        currentModule,
        modules,
        selectedSlot,
        setCurrentModule,
        slotComponent,
        submitDisabled: !selectedSlot,
        onSubmit,
        onBack,

        selectModuleModalOpen,
        setSelectModuleModalOpen
    };
};

export default usePickUpConfirmOpenSlot;
